import type { ReactNode } from "react";
import { CheckIcon } from "./icons";

// Checkbox — DS §4.12 (.fc-check-inline). 20px box, radius 5, 1.5px border.
// 20 rather than 16 on the operator's eye: at 16 the box read as small beside
// 14px text. The hit target did not change and never needed to — the whole
// label row is the button, 40px tall, already past WCAG 2.5.8's 24px floor.
// Square, not 20x24: a rectangular checkbox reads as a field or a button, and
// the glyph has no honest centre in one.
// checked = --success box + white ✓; indeterminate = white dash; disabled = --muted.
//
// --success, not --cta, marks the committed state across the selection controls
// (radio, checkbox, switch). --cta is the colour of the ONE action a screen is
// pushing you toward; a checked box is not that — it is a thing you have already
// settled, and it should not compete with the primary button next to it. This
// also lines the controls up with the fields, which hover sage and focus
// --success, so a green that means "you did this" reads the same everywhere.

export interface CheckboxProps {
    checked: boolean;
    indeterminate?: boolean;
    disabled?: boolean;
    onChange: (c: boolean) => void;
    label?: ReactNode;
    /** `lg` (24px box, 16px gap) is for a card whose label runs to a title plus
     *  a description; `md` is the inline default. Opt-in, so every existing
     *  call site is unaffected. */
    size?: "md" | "lg";
}

interface CheckboxFaceProps {
    checked: boolean;
    indeterminate?: boolean;
    disabled?: boolean;
    label?: ReactNode;
    multiline?: boolean;
    size?: "md" | "lg";
}

/**
 * The box and its label, WITHOUT the button around them.
 *
 * Exists so `CheckableCard` can put this inside a button of its own that also
 * carries the card's padding. It cannot render `Checkbox` for that: a button
 * inside a button is invalid HTML, and the alternative it used instead — a
 * plain div card with the Checkbox stretched by `[&>button]:w-full` — left the
 * card's own p-4 ring outside the click target. `w-full` fills the CONTENT box;
 * the padding is not in it. So the row looked clickable to the edge and was not.
 *
 * Presentation only: it carries no handler and no role. Whichever element wraps
 * it owns the semantics.
 */
export function CheckboxFace({
    checked,
    indeterminate,
    disabled,
    label,
    multiline,
    size = "md",
}: CheckboxFaceProps) {
    const filled = checked || indeterminate;
    const lg = size === "lg";
    return (
        <span
            className={[
                "inline-flex w-full text-left",
                lg ? "gap-4" : "gap-3",
                multiline ? "items-start" : "items-center",
            ].join(" ")}
        >
            <span
                aria-hidden
                className={[
                    // Corner on --radius-sm: the 5px this hardcoded is not on the
                    // radius ladder (2026-09-15). md stays 20px; lg (24px) is opt-in.
                    "flex shrink-0 items-center justify-center rounded-[var(--radius-sm)] border-[1.5px]",
                    lg ? "h-6 w-6" : "h-5 w-5",
                    "transition-[background-color,border-color] duration-[var(--dur-fast)] ease-[var(--ease-out)]",
                    multiline ? "mt-0.5" : "",
                    disabled
                        ? "border-border bg-surface"
                        : filled
                          ? "border-interactive bg-interactive text-surface"
                          : "border-border bg-surface",
                ].join(" ")}
            >
                {indeterminate ? (
                    <span className="h-[1.5px] w-2.5 rounded-[1px] bg-current" />
                ) : checked ? (
                    <CheckIcon size={lg ? 14 : 12} color="currentColor" />
                ) : null}
            </span>
            {label && <span className="[font:var(--type-body)]">{label}</span>}
        </span>
    );
}

export function Checkbox({ checked, indeterminate, disabled, onChange, label, size }: CheckboxProps) {
    return (
        <button
            type="button"
            role="checkbox"
            aria-checked={indeterminate ? "mixed" : checked}
            disabled={disabled}
            onClick={() => !disabled && onChange(!checked)}
            className={[
                "inline-flex items-center text-left select-none",
                disabled ? "cursor-not-allowed text-tertiary" : "cursor-pointer text-primary",
            ].join(" ")}
        >
            <CheckboxFace
                checked={checked}
                indeterminate={indeterminate}
                disabled={disabled}
                label={label}
                size={size}
            />
        </button>
    );
}
